import React from 'react';
import { Button } from '@this/shared/ui/inputs/button';
import { Fetcher, camelToSnakeObject } from '@this/src/util';
import type ReserveInfo from '@this/domain/reserve_info2';

interface Props {
  reserveInfo: ReserveInfo;
  onClose: () => void;
  onCompleted?: () => void;
}

type State = {
  submitting: boolean;
  completed: boolean;
  error: string | null;
};

class TabiregiRegistrationModal extends React.Component<Props, State> {
  state: State = {
    submitting: false,
    completed: false,
    error: null
  };

  handleRegister = async () => {
    const { reserveInfo, onCompleted } = this.props;

    this.setState({ submitting: true, error: null });

    try {
      await Fetcher.post(
        '/tabiregi_consents.json',
        camelToSnakeObject({ searchQueryId: reserveInfo.searchQueryId })
      );
      this.setState({ completed: true });
      if (onCompleted) onCompleted();
    } catch {
      this.setState({ error: 'たびレジ登録に失敗しました。もう一度お試しください。' });
    } finally {
      this.setState({ submitting: false });
    }
  };

  render() {
    const { reserveInfo, onClose } = this.props;
    const { submitting, completed, error } = this.state;
    const styles = getStyles();
    const user = reserveInfo.user;
    const userName = user.name || '';
    const userEmail = user.email || '';

    return (
      <div style={styles.overlay} onClick={onClose} data-testid="tabiregi-registration-modal-overlay">
        <div style={styles.modal} onClick={e => e.stopPropagation()} data-testid="tabiregi-registration-modal">
          <h3 style={styles.modalTitle}>たびレジ登録フォーム</h3>
          {completed ? (
            <div style={styles.modalBody}>
              <p style={styles.successMessage} data-testid="tabiregi-registration-success">
                たびレジへの登録が完了しました。
              </p>
              <div style={styles.buttonArea}>
                <Button onClick={onClose}>閉じる</Button>
              </div>
            </div>
          ) : (
            <div style={styles.modalBody}>
              <p style={styles.description} data-testid="tabiregi-registration-description">
                {userName}({userEmail}) の出張日程、滞在先、連絡先をたびレジに登録します。
              </p>
              {error && (
                <p style={styles.errorMessage} data-testid="tabiregi-registration-error">
                  {error}
                </p>
              )}
              <div style={styles.buttonArea}>
                <Button color="sub" onClick={onClose} disabled={submitting}>
                  戻る
                </Button>
                <Button
                  onClick={this.handleRegister}
                  loading={submitting}
                  data-testid="tabiregi-registration-submit"
                >
                  登録
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

function getStyles() {
  return {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    } as React.CSSProperties,
    modal: {
      background: '#fff',
      borderRadius: '8px',
      padding: '24px',
      maxWidth: '500px',
      width: '90%',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    } as React.CSSProperties,
    modalTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '16px',
      paddingBottom: '12px',
      borderBottom: '1px solid #ddd'
    } as React.CSSProperties,
    modalBody: {
      fontSize: '14px',
      lineHeight: 1.6
    } as React.CSSProperties,
    description: {
      marginBottom: '20px'
    } as React.CSSProperties,
    successMessage: {
      marginBottom: '20px',
      color: '#28a745',
      fontWeight: 'bold'
    } as React.CSSProperties,
    errorMessage: {
      marginBottom: '12px',
      color: '#dc3545',
      fontSize: '13px'
    } as React.CSSProperties,
    buttonArea: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      marginTop: '16px'
    } as React.CSSProperties
  } as const;
}

export default TabiregiRegistrationModal;
